// src/pages/public/checkout/OrderSuccess.jsx
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { WarningIcon } from '../../../public/icons';
import { CheckImage } from '../../../public/images/memoir';
import paymentService from '../../../services/paymentService';
import orderService from '../../../services/orderService';

const HERO_IMAGE =
  'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=1200&auto=format&fit=crop&q=80';

const OrderSuccess = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // Paystack sends either `reference` or `trxref`. Also fall back to localStorage
  // (memoir flow stores it before redirecting away).
  const reference =
    params.get('reference') ||
    params.get('trxref') ||
    localStorage.getItem('kapta_pending_memoir_ref') ||
    localStorage.getItem('kapta_pending_payment_ref');

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);           // regular order
  const [memoirOrder, setMemoirOrder] = useState(null); // memoir order
  const [error, setError] = useState('');
  const [expandedItem, setExpandedItem] = useState(null);

  /* ───────── Load + verify ───────── */
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        if (!reference) {
          setError('Missing payment reference');
          return;
        }

        // 1. Verify payment — the backend auto-detects subjectModel
        const res = await paymentService.verify(reference);
        if (cancelled) return;

        if (!res.success || res.status !== 'success') {
          setError(res.message || 'Payment was not successful');
          return;
        }

        /* ── Memoir order path ── */
        if (res.memoirOrder) {
          if (!cancelled) setMemoirOrder(res.memoirOrder);
          // Clean up the pending reference now that we've verified
          try {
            localStorage.removeItem('kapta_pending_memoir_ref');
            localStorage.removeItem('kapta_pending_memoir_order');
          } catch (_) {}
          return;
        }

        /* ── Regular order path ── */
        let loadedOrder = res.order;

        // If verify didn't include items, fetch the full order
        if (
          loadedOrder &&
          (!loadedOrder.items || loadedOrder.items.length === 0)
        ) {
          try {
            const fullOrder = await orderService.getById(loadedOrder.id);
            if (fullOrder) loadedOrder = { ...loadedOrder, ...fullOrder };
          } catch (fetchErr) {
            console.warn('Could not load full order:', fetchErr);
          }
        }

        if (!cancelled) setOrder(loadedOrder);

        try {
          localStorage.removeItem('kapta_pending_payment_ref');
        } catch (_) {}
      } catch (err) {
        if (cancelled) return;
        setError(
          err.response?.data?.message ||
            err.message ||
            'Failed to load order'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [reference]);

  /* ───────── Per-item detail rows (regular orders) ───────── */
  const renderItemDetails = (item) => {
    const rows = [];
    const c = item.customization || {};

    if (item.magnetName) rows.push({ label: 'Magnet', value: item.magnetName });
    if (item.collectionName)
      rows.push({ label: 'Collection', value: item.collectionName });
    if (item.isCustom) rows.push({ label: 'Custom Gift Box', value: 'Yes' });

    if (c.setSize) rows.push({ label: 'Set', value: c.setSize });
    if (c.magnetType)
      rows.push({
        label: 'Magnet Type',
        value: c.magnetType === 'pre-curated' ? 'Pre-curated' : 'Uploaded Photos'
      });
    if (c.magnetSetCount)
      rows.push({ label: 'Magnet Sets', value: c.magnetSetCount });
    if (c.frameCount) rows.push({ label: 'Frames', value: c.frameCount });
    if (c.color) rows.push({ label: 'Colour', value: c.color });

    if (c.extras?.selectedExtras?.length) {
      rows.push({
        label: 'Extras',
        value: c.extras.selectedExtras.join(', ')
      });
    }
    if (c.extras?.selectedAudioItem) {
      rows.push({
        label: 'Audio Gift Item',
        value:
          c.extras.selectedAudioItem === 'teddy-bear'
            ? 'Teddy Bear'
            : 'Wooden Alarm Clock'
      });
    }

    if (c.futureGift?.futureGifts?.length) {
      rows.push({
        label: 'Future Gifts',
        value: `${c.futureGift.futureGifts.length} scheduled`
      });
      c.futureGift.futureGifts.forEach((fg, i) => {
        rows.push({
          label: `  • ${fg.title || `Gift ${i + 1}`}`,
          value: `${fg.deliveryDate} • ${fg.giftType}`
        });
      });
    }

    if (item.giftItemLabel) rows.push({ label: 'Gift Item', value: item.giftItemLabel });
    if (item.audioSource) rows.push({ label: 'Audio Source', value: item.audioSource });
    if (item.audioFile?.name) rows.push({ label: 'Audio', value: item.audioFile.name });
    if (item.audioUrl) rows.push({ label: 'Audio URL', value: item.audioUrl });

    if (item.personalizedMessage) {
      rows.push({
        label: 'Message',
        value:
          item.personalizedMessage.length > 80
            ? item.personalizedMessage.slice(0, 80) + '…'
            : item.personalizedMessage
      });
    }
    if (c.magnets?.length) {
      rows.push({ label: 'Magnets Added', value: `${c.magnets.length}` });
    }
    return rows;
  };

  /* ───────── Loading ───────── */
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCF7EE] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#D2A146] mx-auto mb-4" />
          <p className="text-sm text-[#6B635D]">Confirming your order…</p>
        </div>
      </div>
    );
  }

  /* ───────── Error ───────── */
  if (error) {
    return (
      <div className="min-h-screen bg-[#FCF7EE] flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <WarningIcon size={40} color="#A82121" />
          <h2 className="text-xl font-medium text-[#1C2A46] mt-4 mb-2">
            Something went wrong
          </h2>
          <p className="text-sm text-[#6B635D] mb-6">{error}</p>
          <Link
            to="/checkout"
            className="inline-block bg-[#D2A146] text-white px-6 py-3 font-medium hover:bg-[#C49660] transition-colors"
          >
            Back to Checkout
          </Link>
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════
     MEMOIR ORDER SUCCESS VIEW
     ═══════════════════════════════════════════════════════ */
  if (memoirOrder) {
    const categoryName =
      memoirOrder.category?.name || 'Your Memoir';
    const amountPaid = Number(memoirOrder.totalAmount || 0);
    const paidDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const themeLabel =
      memoirOrder.theme === 'wine' ? 'Wine' : 'Midnight Blue';
    const freqLabel = {
      weekly: 'Weekly',
      daily: 'Daily',
      biweekly: 'Bi-weekly'
    }[memoirOrder.questionFrequency] || 'Weekly';

    return (
      <div className="min-h-screen bg-[#FCF7EE]">
        <div className="container mx-auto max-w-6xl px-4 sm:px-6 pt-28 sm:pt-32 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            {/* Left: Hero */}
            <div className="w-full">
              <div className="w-full aspect-[4/5] overflow-hidden bg-[#E8E0D5]">
                <img
                  src={CheckImage}
                  alt="Your memoir order"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Right: Content */}
            <div className="w-full text-left">
              {/* Success icon */}
              <div className="w-12 h-12 rounded-full bg-[#E7F4ED] flex items-center justify-center mb-5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#2F8F5B"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>

              <h1 className="text-3xl sm:text-4xl font-bold text-[#1C2A46] mb-3">
                Your Memoir Journey Has Begun!
              </h1>
              <p className="text-sm sm:text-base text-[#6B635D] leading-relaxed mb-2">
                Payment confirmed. We'll send your first guided question within
                24 hours. Welcome to the Kapta Memoir experience.
              </p>
              <p className="text-xs text-[#6B635D] mb-10">
                Paid on {paidDate} • Ref: {reference?.slice(0, 24)}…
              </p>

              {/* Order Summary header */}
              <div className="flex items-center justify-between mb-6 pt-6 border-t border-[#DBDBDB]">
                <h2 className="text-lg sm:text-xl font-medium text-[#1C2A46]">
                  Memoir Summary
                </h2>
                <span className="text-sm text-[#6B635D]">
                  Order:{' '}
                  <span className="text-[#1C2A46] font-medium">
                    #{memoirOrder.orderNumber}
                  </span>
                </span>
              </div>

              {/* Memoir details card */}
              <div className="bg-white border border-[#DBDBDB] p-5 mb-6">
                <h3 className="text-base font-medium text-[#1C2A46] mb-4">
                  {memoirOrder.memoirTitle || 'Untitled Memoir'}
                </h3>
                {memoirOrder.memoirSubtitle && (
                  <p className="text-sm italic text-[#6B635D] mb-4">
                    {memoirOrder.memoirSubtitle}
                  </p>
                )}

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#6B635D]">Category</span>
                    <span className="text-[#1C2A46] font-medium">
                      {categoryName}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6B635D]">Pages</span>
                    <span className="text-[#1C2A46]">
                      {memoirOrder.pages || 100}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6B635D]">Copies</span>
                    <span className="text-[#1C2A46]">
                      {memoirOrder.copies || 1}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6B635D]">Theme</span>
                    <span className="text-[#1C2A46]">{themeLabel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6B635D]">Question Frequency</span>
                    <span className="text-[#1C2A46]">{freqLabel}</span>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-3 mt-3 border-t border-[#DBDBDB]">
                  <span className="text-base font-medium text-[#1C2A46]">
                    Amount Paid
                  </span>
                  <span className="text-lg font-medium text-[#1C2A46]">
                    ₦{amountPaid.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Delivery address */}
              {memoirOrder.deliveryDetails && (
                <div className="mt-6 pt-6 border-t border-[#DBDBDB]">
                  <h3 className="text-sm font-medium text-[#1C2A46] mb-2">
                    Delivery Address
                  </h3>
                  <p className="text-xs text-[#6B635D] leading-relaxed">
                    {memoirOrder.deliveryDetails.firstName}{' '}
                    {memoirOrder.deliveryDetails.lastName}
                    <br />
                    {memoirOrder.deliveryDetails.address}
                    <br />
                    {memoirOrder.deliveryDetails.city},{' '}
                    {memoirOrder.deliveryDetails.state}
                    <br />
                    {memoirOrder.deliveryDetails.country}
                  </p>
                </div>
              )}

              {/* Next step */}
              <div className="bg-[#FCF7EE] border border-[#E4E4E4] p-4 mt-6">
                <p className="text-xs text-[#6B635D] leading-relaxed">
                  <strong className="text-[#1C2A46]">📖 What happens next:</strong>{' '}
                  We'll email your first memoir question. You can answer in
                  writing or as a voice note. Over{' '}
                  {memoirOrder.totalWeeks || 12} weeks, we'll help you shape
                  your story into a keepsake book.
                </p>
              </div>

              {/* CTA */}
              <button
                onClick={() => {
                  // Logged-in users go to their memoir dashboard
                  // Guests go to the track page
                  if (localStorage.getItem('kapta_token')) {
                    navigate(`/memoirs/${memoirOrder.id}`);
                  } else {
                    navigate(
                      `/memoirs/track?orderNumber=${encodeURIComponent(
                        memoirOrder.orderNumber
                      )}&email=${encodeURIComponent(
                        memoirOrder.deliveryDetails?.email || ''
                      )}`
                    );
                  }
                }}
                className="w-full mt-8 py-4 bg-[#D2A146] text-white text-base font-medium hover:bg-[#C49660] transition-colors"
              >
                Go to My Memoir
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════
     REGULAR ORDER SUCCESS VIEW
     ═══════════════════════════════════════════════════════ */
  const orderNumber = order?.orderNumber || '—';
  const items = order?.items || [];
  const subtotal = order?.subtotal ?? 0;
  const memoryIncluded = order?.memoryIncluded ?? 0;
  const shippingFee = order?.shippingFee ?? 0;
  const total = order?.total ?? 0;

  const paidDate = order?.paidAt
    ? new Date(order.paidAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : null;

  return (
    <div className="min-h-screen bg-[#FCF7EE]">
      <div className="container mx-auto max-w-6xl px-4 sm:px-6 pt-28 sm:pt-32 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Left: Hero */}
          <div className="w-full">
            <div className="w-full aspect-[4/5] overflow-hidden bg-[#E8E0D5]">
              <img
                src={HERO_IMAGE}
                alt="Your Kapta order"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Right: Content */}
          <div className="w-full text-left">
            {/* Success icon */}
            <div className="w-12 h-12 rounded-full bg-[#E7F4ED] flex items-center justify-center mb-5">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#2F8F5B"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>

            <h1 className="text-3xl sm:text-4xl font-bold text-[#1C2A46] mb-3">
              Thank You For Your Order!
            </h1>
            <p className="text-sm sm:text-base text-[#6B635D] leading-relaxed mb-2">
              Your Kapta product is on its way. You can track your order
              anytime from your account.
            </p>
            {paidDate && (
              <p className="text-xs text-[#6B635D] mb-10">
                Paid on {paidDate} • Ref: {reference?.slice(0, 24)}…
              </p>
            )}

            {/* Order Summary header */}
            <div className="flex items-center justify-between mb-6 pt-6 border-t border-[#DBDBDB]">
              <h2 className="text-lg sm:text-xl font-medium text-[#1C2A46]">
                Order Summary
              </h2>
              <span className="text-sm text-[#6B635D]">
                Order number:{' '}
                <span className="text-[#1C2A46] font-medium">
                  #{orderNumber}
                </span>
              </span>
            </div>

            {/* Items */}
            {items.length > 0 ? (
              <div className="divide-y divide-[#DBDBDB]">
                {items.map((item, idx) => {
                  const details = renderItemDetails(item);
                  const isExpanded = expandedItem === idx;

                  return (
                    <div key={item._id || idx} className="py-5">
                      <div className="flex gap-5">
                        <div className="w-20 h-24 flex-shrink-0 bg-[#F5F5F5] overflow-hidden">
                          {item.image && (
                            <img
                              src={item.image}
                              alt={item.title}
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-medium text-[#1C2A46]">
                            {item.title}
                          </h3>
                          {item.description && (
                            <p className="text-xs italic text-[#6B635D] mt-0.5">
                              {item.description}
                            </p>
                          )}
                          <p className="text-xs text-[#6B635D] mt-2">
                            Qty: {item.quantity}
                          </p>
                          <p className="text-sm font-medium text-[#1C2A46] mt-1">
                            ₦
                            {(
                              item.lineTotal ||
                              item.unitPrice * item.quantity
                            ).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {details.length > 0 && (
                        <button
                          onClick={() =>
                            setExpandedItem(isExpanded ? null : idx)
                          }
                          className="mt-3 text-xs text-[#D2A146] hover:text-[#C49660] underline"
                        >
                          {isExpanded ? 'Hide details' : 'View details'}
                        </button>
                      )}

                      {isExpanded && details.length > 0 && (
                        <div className="mt-3 bg-[#F5F5F5] border border-[#E4E4E4] p-3 space-y-1.5">
                          {details.map((row, i) => (
                            <div
                              key={i}
                              className="flex justify-between gap-4 text-xs"
                            >
                              <span className="text-[#6B635D]">
                                {row.label}
                              </span>
                              <span className="text-[#1C2A46] text-right break-words max-w-[60%]">
                                {row.value}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-[#6B635D] py-6">
                Your order items have been saved. Open the order for full
                details.
              </div>
            )}

            {/* Totals */}
            {items.length > 0 && (
              <div className="border-t border-[#DBDBDB] pt-6 mt-2 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-[#6B635D]">Subtotal</span>
                  <span className="text-[#1C2A46]">
                    ₦{subtotal.toLocaleString()}
                  </span>
                </div>
                {memoryIncluded > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#6B635D]">Memory Included</span>
                    <span className="text-[#1C2A46]">
                      ₦{memoryIncluded.toLocaleString()}
                    </span>
                  </div>
                )}
                {shippingFee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#6B635D]">Shipping Fee</span>
                    <span className="text-[#1C2A46]">
                      ₦{shippingFee.toLocaleString()}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-3 border-t border-[#DBDBDB]">
                  <span className="text-base font-medium text-[#1C2A46]">
                    Total
                  </span>
                  <span className="text-lg font-medium text-[#1C2A46]">
                    ₦{total.toLocaleString()}
                  </span>
                </div>
              </div>
            )}

            {/* Delivery address */}
            {order?.delivery && (
              <div className="mt-8 pt-6 border-t border-[#DBDBDB]">
                <h3 className="text-sm font-medium text-[#1C2A46] mb-2">
                  Delivery Address
                </h3>
                <p className="text-xs text-[#6B635D] leading-relaxed">
                  {order.customer?.firstName} {order.customer?.lastName}
                  <br />
                  {order.delivery.address}
                  <br />
                  {order.delivery.city}, {order.delivery.state}
                  <br />
                  {order.delivery.country}
                  {order.delivery.deliveryNote && (
                    <>
                      <br />
                      <span className="italic">
                        Note: {order.delivery.deliveryNote}
                      </span>
                    </>
                  )}
                </p>
              </div>
            )}

            {/* View Order button */}
            <button
              onClick={() => navigate('/account/orders/all')}
              className="w-full mt-8 py-4 bg-[#D2A146] text-white text-base font-medium hover:bg-[#C49660] transition-colors"
            >
              View Order
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;